import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import './Auth.css';

function ResetPassword() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [token, setToken] = useState('');
    const [passwordStrength, setPasswordStrength] = useState({
        score: 0,
        label: '',
        color: '',
        checks: {
            length: false,
            uppercase: false,
            lowercase: false,
            number: false,
            special: false
        }
    });

    const [verifying, setVerifying] = useState(true);

    useEffect(() => {
        const verifyToken = async () => {
            const tokenFromUrl = searchParams.get('token');
            if (!tokenFromUrl) {
                setError('無効なリセットリンクです。パスワードのリセットを再度リクエストしてください。');
                setVerifying(false);
                return;
            }
            
            setToken(tokenFromUrl);
            
            try {
                const response = await fetch(`${API_ENDPOINTS.VERIFY_TOKEN}?token=${tokenFromUrl}`);
                const data = await response.json();
                
                if (!data.valid) {
                    setError(data.error || 'このリセットリンクは無効または期限切れです。');
                }
            } catch (err) {
                setError('接続エラーが発生しました。リセットリンクを確認できません。');
            } finally {
                setVerifying(false);
            }
        };
        
        verifyToken();
    }, [searchParams]);

    // Calculate password strength
    useEffect(() => {
        if (!password) {
            setPasswordStrength({
                score: 0,
                label: '',
                color: '',
                checks: {
                    length: false,
                    uppercase: false,
                    lowercase: false,
                    number: false,
                    special: false
                }
            });
            return;
        }

        const checks = {
            length: password.length >= 8,
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            number: /\d/.test(password),
            special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
        };

        const score = Object.values(checks).filter(Boolean).length;

        let label = '';
        let color = '';

        if (score === 0) {
            label = '';
            color = '';
        } else if (score <= 2) {
            label = '弱い';
            color = '#ef4444';
        } else if (score === 3) {
            label = '普通';
            color = '#f59e0b';
        } else if (score === 4) {
            label = '良い';
            color = '#3b82f6';
        } else {
            label = '強い';
            color = '#10b981';
        }

        setPasswordStrength({ score, label, color, checks });
    }, [password]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!token) {
            setError('無効なリセットリンクです');
            return;
        }

        if (password !== confirmPassword) {
            setError('パスワードが一致しません');
            return;
        }

        if (passwordStrength.score < 5) {
            setError('パスワードの要件をすべて満たしてください');
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(API_ENDPOINTS.RESET_PASSWORD, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token, password }),
            });

            const data = await response.json();

            if (response.ok) {
                navigate('/login', { state: { message: 'パスワードが正常にリセットされました。新しいパスワードでログインしてください。' } });
            } else {
                setError(data.error || 'パスワードのリセットに失敗しました');
            }
        } catch (err) {
            setError('接続エラーが発生しました。ネットワーク接続を確認してください。');
        } finally {
            setLoading(false);
        }
    };

    const renderContent = () => {
        if (verifying) {
            return (
                <div className="verifying-container">
                    <span className="spinner"></span>
                    <p>リセットリンクを確認中...</p>
                </div>
            );
        }

        if (error && !token) {
            return (
                <div className="error-container">
                    <div className="error-message">{error}</div>
                    <div className="auth-footer" style={{ marginTop: '20px' }}>
                        <p><Link to="/forgot-password">リセットを再リクエストする</Link></p>
                    </div>
                </div>
            );
        }

        return (
            <>
                <div className="auth-header">
                    <h2>パスワードのリセット</h2>
                    <p>新しいパスワードを入力してください</p>
                </div>

                {error && <div className="error-message">{error}</div>}

                <form onSubmit={handleSubmit} className="auth-form">
                    <div className="form-group">
                        <label>新しいパスワード</label>
                        <div className="password-input-wrapper">
                            <input
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                required
                                disabled={loading || !token}
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                                tabIndex="-1"
                            >
                                {showPassword ? '👁️' : '👁️‍🗨️'}
                            </button>
                        </div>

                        {/* Password Strength Meter */}
                        {password && (
                            <div className="password-strength">
                                <div className="strength-bars">
                                    {[1, 2, 3, 4, 5].map((bar) => (
                                        <div
                                            key={bar}
                                            className={`strength-bar ${bar <= passwordStrength.score ? 'active' : ''}`}
                                            style={{
                                                backgroundColor: bar <= passwordStrength.score ? passwordStrength.color : '#e5e7eb'
                                            }}
                                        ></div>
                                    ))}
                                </div>
                                {passwordStrength.label && (
                                    <span className="strength-label" style={{ color: passwordStrength.color }}>
                                        {passwordStrength.label}
                                    </span>
                                )}
                            </div>
                        )}

                        {/* Password Requirements */}
                        {password && (
                            <div className="password-requirements">
                                <div className={`requirement ${passwordStrength.checks.length ? 'met' : ''}`}>
                                    {passwordStrength.checks.length ? '✓' : '○'} 8文字以上
                                </div>
                                <div className={`requirement ${passwordStrength.checks.uppercase ? 'met' : ''}`}>
                                    {passwordStrength.checks.uppercase ? '✓' : '○'} 大文字を1文字以上
                                </div>
                                <div className={`requirement ${passwordStrength.checks.lowercase ? 'met' : ''}`}>
                                    {passwordStrength.checks.lowercase ? '✓' : '○'} 小文字を1文字以上
                                </div>
                                <div className={`requirement ${passwordStrength.checks.number ? 'met' : ''}`}>
                                    {passwordStrength.checks.number ? '✓' : '○'} 数字を1文字以上
                                </div>
                                <div className={`requirement ${passwordStrength.checks.special ? 'met' : ''}`}>
                                    {passwordStrength.checks.special ? '✓' : '○'} 記号を1文字以上
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="form-group">
                        <label>新しいパスワード（確認用）</label>
                        <div className="password-input-wrapper">
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="••••••••"
                                required
                                disabled={loading || !token}
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                tabIndex="-1"
                            >
                                {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                            </button>
                        </div>
                        {confirmPassword && password !== confirmPassword && (
                            <p style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '6px' }}>
                                パスワードが一致しません
                            </p>
                        )}
                    </div>

                    <button
                        type="submit"
                        className="btn-primary btn-block"
                        disabled={loading || !token || (password && passwordStrength.score < 5) || password !== confirmPassword}
                    >
                        {loading ? (
                            <>
                                <span className="spinner"></span>
                                リセット中...
                            </>
                        ) : 'パスワードをリセット'}
                    </button>
                </form>

                <div className="auth-footer">
                    <p>パスワードを覚えていますか？ <Link to="/login">ログイン</Link></p>
                </div>
            </>
        );
    };

    return (
        <div className="page-container auth-page">
            <Navbar />
            <div className="auth-card glass">
                {renderContent()}
            </div>
        </div>
    );
}

export default ResetPassword;
